/**
 * Rough download speed for this tab, from real image transfers.
 *
 * Tiers drive how large a picture the viewer and the page grids ask for:
 * - slow: under 150 KB/s, or Save-Data / 2G
 * - medium: under 600 KB/s, or 3G
 * - fast: everything else, and the default before anything is measured
 *
 * A slower tier applies at once. A faster tier needs three faster samples in a
 * row, so one quick cached file does not bounce a struggling link back to 3000px.
 */

export const TIERS = ['slow', 'medium', 'fast'];

const SLOW_BPS = 150 * 1024;
const MEDIUM_BPS = 600 * 1024;
const MIN_BYTES = 20000;
const MIN_MS = 50;
const SMOOTHING = 0.3;
const UPGRADE_STREAK = 3;
const STALL_FLOOR_BPS = 50 * 1024;
const IMAGE_PATH = /\.(webp|jpe?g|png|avif|gif)(\?|#|$)/i;

let estimateBps = null;
let tier = null;
let pendingUpgrade = null;
let upgradeStreak = 0;
let forcedTier = null;
let observer = null;
let connectionWatched = false;
const listeners = new Set();

function tierFor(bps) {
    if (bps < SLOW_BPS) return 'slow';
    if (bps < MEDIUM_BPS) return 'medium';
    return 'fast';
}

function rank(name) {
    return TIERS.indexOf(name);
}

function hintTier() {
    if (typeof navigator === 'undefined') return null;
    const connection = navigator.connection;
    if (!connection) return null;
    if (connection.saveData) return 'slow';
    const type = connection.effectiveType;
    if (type === 'slow-2g' || type === '2g') return 'slow';
    if (type === '3g') return 'medium';
    return null;
}

function notify() {
    listeners.forEach((fn) => {
        try {
            fn(connectionTier());
        } catch {
            // A broken listener must not stop the others.
        }
    });
}

function setTier(next) {
    if (next === tier) return;
    tier = next;
    notify();
}

function applyMeasured(bps) {
    const measured = tierFor(bps);
    if (tier == null) {
        setTier(measured);
        return;
    }
    if (rank(measured) < rank(tier)) {
        pendingUpgrade = null;
        upgradeStreak = 0;
        setTier(measured);
        return;
    }
    if (rank(measured) === rank(tier)) {
        pendingUpgrade = null;
        upgradeStreak = 0;
        return;
    }
    if (pendingUpgrade === measured || (pendingUpgrade && rank(measured) >= rank(pendingUpgrade))) {
        upgradeStreak += 1;
    } else {
        pendingUpgrade = measured;
        upgradeStreak = 1;
    }
    if (upgradeStreak >= UPGRADE_STREAK) {
        const next = pendingUpgrade;
        pendingUpgrade = null;
        upgradeStreak = 0;
        setTier(next);
    }
}

/**
 * One finished transfer. Small or near-instant responses are cache hits or
 * headers-only and say nothing about the link.
 * @param {number} bytes
 * @param {number} ms
 */
export function recordSample(bytes, ms) {
    const size = Number(bytes);
    const elapsed = Number(ms);
    if (!Number.isFinite(size) || !Number.isFinite(elapsed)) return;
    if (size < MIN_BYTES || elapsed < MIN_MS) return;
    const bps = (size * 1000) / elapsed;
    estimateBps = estimateBps == null ? bps : (1 - SMOOTHING) * estimateBps + SMOOTHING * bps;
    applyMeasured(bps);
}

/**
 * A transfer that has not finished after `elapsedMs`. Bytes received so far give
 * a real rate; with nothing received yet it counts as a 50 KB/s sample, so the
 * viewer drops to smaller files before the stalled one ever completes.
 * @param {number} elapsedMs
 * @param {number} [bytesSoFar]
 */
export function recordStall(elapsedMs, bytesSoFar = 0) {
    const elapsed = Number(elapsedMs);
    if (!Number.isFinite(elapsed) || elapsed <= 0) return;
    const bps = bytesSoFar > 0 ? (bytesSoFar * 1000) / elapsed : STALL_FLOOR_BPS;
    estimateBps = estimateBps == null ? bps : (1 - SMOOTHING) * estimateBps + SMOOTHING * bps;
    applyMeasured(bps);
}

function onResourceEntries(list) {
    list.getEntries().forEach((entry) => {
        if (!entry || !IMAGE_PATH.test(entry.name || '')) return;
        const type = entry.initiatorType;
        // Viewer fetches report their own samples; counting them here would double them.
        if (type !== 'img' && type !== 'css' && type !== 'other') return;
        const bytes = entry.transferSize || entry.encodedBodySize || 0;
        // Time the transfer itself. From startTime, a file queued behind the
        // others a page loads at once reads as a slow link.
        const from = entry.responseStart > 0 ? entry.responseStart : entry.startTime;
        const ms = (entry.responseEnd || 0) - (from || 0);
        recordSample(bytes, ms);
    });
}

function watch() {
    if (typeof window === 'undefined') return;
    if (!observer && typeof PerformanceObserver === 'function') {
        try {
            observer = new PerformanceObserver(onResourceEntries);
            observer.observe({ type: 'resource', buffered: true });
        } catch {
            observer = null;
        }
    }
    const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (!connectionWatched && connection?.addEventListener) {
        connectionWatched = true;
        connection.addEventListener('change', () => {
            const hint = hintTier();
            if (hint && (tier == null || rank(hint) < rank(tier))) setTier(hint);
        });
    }
}

/** @returns {'slow'|'medium'|'fast'} */
export function connectionTier() {
    if (forcedTier) return forcedTier;
    watch();
    if (tier == null) {
        const hint = hintTier();
        if (hint) tier = hint;
    }
    return tier || 'fast';
}

/** Smoothed bytes per second, or null before any sample. */
export function connectionEstimate() {
    return estimateBps;
}

/** @param {(tier: string) => void} fn */
export function subscribeTier(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

/** Test seam. Null returns to measured behaviour. */
export function setTierForTest(next) {
    forcedTier = TIERS.includes(next) ? next : null;
    notify();
}

export function resetSpeed() {
    estimateBps = null;
    tier = null;
    pendingUpgrade = null;
    upgradeStreak = 0;
    forcedTier = null;
}

/** Widest generated rung worth requesting on this tier. Infinity means no cap. */
export const TIER_WIDTH_CAP = Object.freeze({
    slow: 768,
    medium: 1280,
    fast: Number.POSITIVE_INFINITY,
});

export function tierWidthCap(name = connectionTier()) {
    return TIER_WIDTH_CAP[name] ?? Number.POSITIVE_INFINITY;
}

/** How long one picture download may run before the viewer gives up on it. */
export const TIER_TIMEOUT_MS = Object.freeze({
    slow: 20000,
    medium: 12000,
    fast: 8000,
});
